(() => {
  'use strict';

  if (customElements.get('related-products')) return;

  customElements.define(
    'related-products',
    class RelatedProducts extends HTMLElement {
      connectedCallback() {
        this.loadRecentlyViewedProducts();
        this.deferRelatedProducts();
      }

      disconnectedCallback() {
        this.stopDeferringRelatedProducts();
      }

      deferRelatedProducts() {
        this.relatedProducts = this.querySelector('[data-related-products]');
        if (!this.relatedProducts) return;

        this.loadRelatedProductsOnIntent = () => this.loadRelatedProducts();
        this.loadRelatedProductsOnSectionSelect = (event) => {
          const selectedSectionId = event.detail?.sectionId;
          const ownSectionId = this.getAttribute('data-section-id');
          const containsRelatedProducts = event.target?.contains?.(this);

          if (!selectedSectionId || selectedSectionId === ownSectionId || containsRelatedProducts) {
            this.loadRelatedProducts();
          }
        };

        if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
          this.loadRelatedProducts();
          return;
        }

        this.relatedProductsObserver = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this.loadRelatedProducts();
            }
          },
          { rootMargin: '800px 0px' }
        );

        this.relatedProductsObserver.observe(this);
        this.addEventListener('focusin', this.loadRelatedProductsOnIntent, { once: true });
        this.addEventListener('pointerenter', this.loadRelatedProductsOnIntent, { once: true });
        this.addEventListener('touchstart', this.loadRelatedProductsOnIntent, {
          once: true,
          passive: true,
        });
        document.addEventListener('shopify:section:select', this.loadRelatedProductsOnSectionSelect);
      }

      stopDeferringRelatedProducts() {
        this.relatedProductsObserver?.disconnect();
        this.relatedProductsObserver = null;

        if (this.loadRelatedProductsOnIntent) {
          this.removeEventListener('focusin', this.loadRelatedProductsOnIntent);
          this.removeEventListener('pointerenter', this.loadRelatedProductsOnIntent);
          this.removeEventListener('touchstart', this.loadRelatedProductsOnIntent);
        }

        if (this.loadRelatedProductsOnSectionSelect) {
          document.removeEventListener(
            'shopify:section:select',
            this.loadRelatedProductsOnSectionSelect
          );
        }
      }

      loadRelatedProducts() {
        if (this.relatedProductsRequested) return;

        this.relatedProducts = this.relatedProducts || this.querySelector('[data-related-products]');
        if (!this.relatedProducts) return;

        this.relatedProductsRequested = true;
        this.stopDeferringRelatedProducts();

        const sectionId = this.getAttribute('data-section-id');
        const productId = this.getAttribute('data-product-id');
        const limit = this.getAttribute('data-limit');
        const url = `${theme.routes.product_recommendations_url}?section_id=${sectionId}&limit=${limit}&product_id=${productId}`;

        fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.text();
          })
          .then((html) => {
            const container = document.createElement('div');
            container.innerHTML = html;
            const relatedProducts = container.querySelector('[data-related-products]');

            if (relatedProducts?.querySelector('[data-product-block]')) {
              this.relatedProducts.innerHTML = relatedProducts.innerHTML;
            }
          })
          .catch(() => this.hideRelated());
      }

      loadRecentlyViewedProducts() {
        this.recentlyViewed = this.querySelector('recently-viewed');
        this.recentlyViewed?.addEventListener('theme:recently-viewed:loaded', () => {
          this.handleRecentlyViewedResponse();
        });
      }

      handleRecentlyViewedResponse() {
        const wrapper = this.recentlyViewed.closest('[data-recently-viewed-wrapper]');
        const productBlocks = this.recentlyViewed.querySelectorAll('[data-product-block]');
        const animatedItems = this.recentlyViewed.querySelectorAll('[data-aos]');
        const columns = this.recentlyViewed.querySelector('[data-columns]');
        const slider = this.recentlyViewed.querySelector('grid-slider');
        const minimum = parseInt(this.recentlyViewed.getAttribute('data-minimum')) || 1;
        const standaloneHasProducts = !wrapper && productBlocks.length > 0;
        const wrapperHasEnoughProducts = wrapper && productBlocks.length >= minimum;
        const mobileColumns = parseInt(columns?.getAttribute('data-column-mobile'));
        const desktopColumns = parseInt(columns?.getAttribute('data-columns'));

        if (!standaloneHasProducts && !wrapperHasEnoughProducts) return;

        if (wrapperHasEnoughProducts) this.classList.remove('hidden');

        if (slider) {
          requestAnimationFrame(() => {
            slider.dispatchEvent(new CustomEvent('theme:slider:resize', { bubbles: true }));
          });
        }

        if (columns) {
          const columnClasses = {
            1: 'one-whole',
            2: 'one-half',
            3: 'one-third',
            4: 'one-quarter',
          };

          columns.querySelectorAll('[data-slide]').forEach((slide) => {
            slide.classList.toggle('mobile--one-half', mobileColumns === 2);
            slide.classList.toggle('mobile--one-whole', mobileColumns !== 2);
            slide.classList.remove('one-whole', 'one-half', 'one-third', 'one-quarter');
            slide.classList.add(columnClasses[desktopColumns]);
          });
        }

        const animationTargets = [];
        this.recentlyViewed.addEventListener('theme:target:animate', (event) => {
          animationTargets.push(event.detail);
          if (animationTargets.length === animatedItems.length) {
            requestAnimationFrame(() => {
              animatedItems.forEach((item) => item.classList.add('aos-animate'));
            });
          }
        });
      }

      hideRelated() {
        const tab = this.relatedProducts?.closest('[data-tab]');
        const tabsHolder = this.relatedProducts?.closest('[data-tabs-holder]');
        const tabIndex = tab?.dataset.tab;
        const tabLink =
          tabIndex == null ? null : tabsHolder?.querySelector(`[data-tabs-link="${tabIndex}"]`);

        tab?.remove();

        if (tabLink) {
          tabLink.remove();
          tabsHolder.querySelector('[data-tabs-link]')?.dispatchEvent(new Event('click'));
        }
      }
    }
  );
})();
